import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  InputNumber,
  Divider,
  Modal,
  Radio,
  message,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Dragger from "antd/es/upload/Dragger";
import { save, uploadFile } from "../../../../../../features/save/saveSlice";
import { fetchFiles } from "../../../../../../features/fetch/fetchSlice";
import { formatPathDoc, normalizeDateToLocalYear } from "../../../../../../utils";
import dayjs from "dayjs";
import svg46 from '../../../../../../assets/svg/svg46.svg'

const FarmerPayAddModal = ({
  isModalOpen,
  handleCancel,
  handleFetch,
  handleFetch2,
  prodd,
}) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { counties } = useSelector((state) => state.fetch);
  const urlPaymentType = "api/v2/paymentTypes/get";
  const { activeObjexpressions, data } = useSelector((state) => ({
    activeObjexpressions: state.obj.activeObj["expressions"],
    data: state.fetch.data[urlPaymentType] || [],
  }));
  const [countyName, setCountyName] = useState();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const plainOptions = [
    { label: "Confirmed", value: "CONFIRMED" },
    { label: "Not Confirmed", value: "NOT_CONFIRMED" },
  ];

  const [fileUrl, setfileUrl] = useState(prodd?.paymentFileUrl ?? null);
  const [fileName, setfileName] = useState("");

  const [fileLoading, setFileLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const resetFile = () => {
    setFileList([]);
    setfileUrl("");
    setfileName("");
  };

  const props = {
    name: "file",
    multiple: false,
    accept: "*",
    showUploadList: true,
    fileList,

    beforeUpload: async (file) => {
      setFileLoading(true);
      setFileList([{ uid: file.uid, name: file.name, status: "uploading" }]);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await dispatch(uploadFile(formData)).unwrap();
        if (res?.success) {
          toast.success(`${file.name} file attached successfully.`);
          setfileUrl(res?.data?.result);
          setfileName(file.name);
          setFileList([{ uid: file.uid, name: file.name, status: "done" }]);
        } else {
          message.error(`Upload failed: ${res.message}`);
          resetFile();
        }
      } catch (err) {
        message.error(`Upload failed`);
        resetFile();
      } finally {
        setFileLoading(false);
      }

      return false;
    },

    onRemove: () => {
      resetFile();
    },

    onDrop(e) {
      setFileLoading(false);
    },
  };

  const downloadFile = async (item) => {
    try {
      const res = await dispatch(fetchFiles({ file: item?.paymentFileUrl }));
      if (res?.payload) {
        window.open(res.payload, "_blank");
      }
    } catch (error) {
      console.error("Failed to open file:", error);
    }
  };

  const onFinish = async (data) => {
    data.paymentId = prodd?.paymentId ? prodd?.paymentId : null;
    data.paymentProjectId = activeObjexpressions?.projectId;
    data.paymentDate = normalizeDateToLocalYear(data?.paymentDate);
    data.paymentFileUrl = fileUrl;

    const res = await dispatch(
      save({
        ...data,
        url: "api/v2/payments/SaveProjectPayments",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      form.resetFields();
      resetFile();
      await handleFetch();
      await handleFetch2();
      handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    if (prodd) {
      form.setFieldsValue({
        ...prodd,
        paymentDate: prodd?.paymentDate
          ? dayjs(new Date(prodd?.paymentDate))
          : null,
      });
    } else {
      form.resetFields();
    }
  }, [prodd, form]);

  useEffect(() => {
    setfileUrl(prodd?.paymentFileUrl ?? null);
  }, [prodd?.paymentFileUrl]);

  return (
    <Modal
      title={`${
        prodd?.paymentId ? "Update Payment Record" : "Add Payment Record"
      }`}
      open={isModalOpen}
      onOk={onFinish}
      onCancel={() => { resetFile(); form.resetFields(); handleCancel(); }}
      className="!w-[59.5rem]"
      maskClosable={false}
    >
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
      >
        <div className="flex items-start gap-[2.69rem] w-full">
          <div className="w-full">
            <Form.Item
              name="paymentType"
              className=""
              label={"Service"}
              rules={[
                {
                  required: true,
                  message: "Please select service",
                },
              ]}
            >
              <Select
                className=""
                allowClear
                style={{
                  width: "100%",
                }}
                options={
                  data?.length > 0 &&
                  data?.map((item) => ({
                    value: item?.paymentTypeName,
                    label: item?.paymentTypeName,
                  }))
                }
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label?.toLocaleLowerCase() ?? "").includes(
                    input?.toLocaleLowerCase()
                  )
                }
              />
            </Form.Item>
            <Form.Item
              name="paymentCode"
              label={"Transaction ID"}
              rules={[
                {
                  required: true,
                  message: "Please add transaction Id",
                },
              ]}
            >
              <Input className="input" />
            </Form.Item>
            <Form.Item
              name="paymentAmount"
              label={"Amount"}
              rules={[
                {
                  required: true,
                  message: "Please add amount",
                },
              ]}
            >
              <InputNumber
                min={0}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                className="input flex"
              />
            </Form.Item>
            <Form.Item
              name="paymentPayerName"
              label={"Paid By"}
              rules={[
                {
                  required: false,
                  message: "Please add paid by",
                },
              ]}
            >
              <Input className="input" />
            </Form.Item>
            <Form.Item
              label="Payment Date"
              name="paymentDate"
              rules={[
                {
                  required: true,
                  message: "Payment date is required",
                },
              ]}
            >
              <DatePicker format={"YYYY MMM DD"} className="input" />
            </Form.Item>
            <Form.Item
              name="paymentStatus"
              label="Verified"
              rules={[
                {
                  required: false,
                  message: "Verfied field is required",
                },
              ]}
            >
              <Radio.Group options={plainOptions} />
            </Form.Item>
          </div>
          <div className="w-full">
            {" "}
            <Form.Item
              name="paymentFileName"
              className=""
              label={"Document Name"}
              rules={[
                {
                  required: true,
                  message: "Please add document name",
                },
              ]}
            >
              <Input className="input" />
            </Form.Item>
            <Form.Item
              name="file"
              label="Supporting Document"
              rules={[
                {
                  required: false,
                  message: "Please upload a supporting document",
                },
              ]}
            >
              <Dragger className="attachment" {...props}>
                <div className="flex flex-col justify-center">
                  <div className="flex justify-center mb-5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="29"
                      height="29"
                      viewBox="0 0 29 29"
                      fill="none"
                    >
                      <path
                        d="M25.375 18.125V22.9583C25.375 23.5993 25.1204 24.214 24.6672 24.6672C24.214 25.1204 23.5993 25.375 22.9583 25.375H6.04167C5.40073 25.375 4.78604 25.1204 4.33283 24.6672C3.87961 24.214 3.625 23.5993 3.625 22.9583V18.125"
                        stroke="#147CBC"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M20.5423 9.66667L14.5007 3.625L8.45898 9.66667"
                        stroke="#147CBC"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.5 3.625V18.125"
                        stroke="#147CBC"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="ant-upload-text !ml-2">
                    Click to upload
                    <span className="ml-2 text-[#667085]">
                      or drag and drop
                    </span>
                    <br></br>
                    <span className="!mt-[10rem] text-[#667085]">
                      PNG or JPG (max. 800x400px)
                    </span>
                  </p>
                </div>
              </Dragger>
              {
               prodd?.paymentFileUrl && (
                  <div className="flex justify-between items-center mt-[.25rem]">
                  <span
                    onClick={() => downloadFile(prodd)}
                    className="text-darkBlue cursor-pointer" 
                  >
                    {formatPathDoc(prodd?.paymentFileUrl)}
                  </span>
                  <img onClick={()=>setfileUrl(null)} src={svg46} alt="svg46"/>
                  </div>
                )
              } 
             
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-between mt-[48px] ">
          <div className="justify-start"></div>
          <div className="justify-end flex items-center mb-[58px] gap-x-5">
            <div className="w-[150px]">
              <button
                type="button"
                key="back"
                onClick={() => { resetFile(); form.resetFields(); handleCancel(); }}
                className="cstm-btn"
              >
                Cancel
              </button>
            </div>

            <div className="w-[150px]">
              <button
                disabled={saving}
                key="submit"
                type="submit"
                className="cstm-btn-2"
              >
                {saving ? (
                  <Spin />
                ) : prodd?.paymentId ? (
                  "Update Record"
                ) : (
                  "Save Record"
                )}
              </button>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
export default FarmerPayAddModal;
